import axios from 'axios';
import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

// Wraps a click handler; useApi already toasted the error.
export const safe = (fn) => (...a) => Promise.resolve().then(() => fn(...a)).catch(() => {});

export const useApi = () => {
  const { authHeader } = useAuth();
  const call = useCallback(async (method, path, data, opts = {}) => {
    try {
      const r = await axios({ method, url: `${API}${path}`, data, headers: authHeader(), ...opts });
      return r.data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Something went wrong. Please try again.');
      throw e;
    }
  }, [authHeader]);
  const blob = useCallback((path) => call('get', path, null, { responseType: 'blob' }), [call]);
  const open = useCallback(safe(async (path) => {
    const b = await blob(path);
    window.open(URL.createObjectURL(b), '_blank');
  }), [blob]);
  const download = useCallback(safe(async (path, name) => {
    const b = await blob(path);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b); a.download = name; a.click();
  }), [blob]);
  return { call, blob, open, download };
};

export const rs = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
export const field = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100';
export const btn = 'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50';
export const primary = `${btn} bg-teal-600 text-white hover:bg-teal-700`;
export const ghost = `${btn} border border-slate-200 bg-white text-slate-700 hover:border-slate-400`;

const CHIP = {
  draft: 'bg-slate-100 text-slate-700', sent: 'bg-sky-100 text-sky-800', partly_received: 'bg-amber-100 text-amber-800',
  received: 'bg-emerald-100 text-emerald-800', closed: 'bg-slate-200 text-slate-700', cancelled: 'bg-red-100 text-red-700',
  unpaid: 'bg-red-100 text-red-700', part_paid: 'bg-amber-100 text-amber-800', paid: 'bg-emerald-100 text-emerald-800',
};
export const Chip = ({ s }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${CHIP[s] || 'bg-slate-100'}`} data-testid={`chip-${s}`}>{String(s).replace('_', ' ')}</span>
);

const useEscape = (fn) => useEffect(() => {
  const h = (e) => e.key === 'Escape' && fn();
  window.addEventListener('keydown', h);
  return () => window.removeEventListener('keydown', h);
}, [fn]);

export const Drawer = ({ title, onClose, children, footer, testid }) => { useEscape(onClose); return (
  <div className="fixed inset-0 z-[60] flex justify-end bg-slate-900/40" onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid={testid}>
    <div className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
      <div className="flex items-center justify-between border-b px-5 py-4">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900" data-testid={`${testid}-close`}>Close</button>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto p-5">{children}</div>
      {footer && <div className="flex justify-end gap-2 border-t bg-slate-50 px-5 py-3">{footer}</div>}
    </div>
  </div>
); };

export const F = ({ label, children, span }) => (
  <label className={`block ${span ? 'sm:col-span-2' : ''}`}>
    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
    {children}
  </label>
);

export const Stat = ({ label, value, tone = 'text-slate-900', testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`mt-1 text-xl font-bold ${tone}`} data-testid={testid}>{value}</p>
  </div>
);
